import { Content, LinkButton, Page } from '@backstage/core-components';
import { SearchContextProvider } from '@backstage/plugin-search-react';
import { Card, Grid, Theme, Typography, makeStyles } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import { UpcomingEventsCard } from '@internal/plugin-devhub-events';
import { MyDayCard } from '@internal/plugin-my-day';
import { DiscoverCard } from './DiscoverCard';
import { FavoritesMenu } from './FavoritesMenu';
import { HelpCard } from './HelpCard';
import { HomeAnnouncementsCard } from './HomeAnnouncementsCard';
import { ServiceStatusStrip } from './ServiceStatusStrip';
import { RecentlyVisitedCard, TopVisitedCard } from './TopVisitedCard';
import { PublishedReleaseNotesCard } from './PublishedReleaseNotesCard';

const GET_STARTED_PATH = '/docs/default/component/developer-corner/DevHub/1_Info/portal_info';

const useStyles = makeStyles((theme: Theme) => {
  // The hero reuses the active design's header artwork; themes without one
  // (Classic) fall back to a CAE blue gradient.
  const pageBackground = theme.page?.backgroundImage;
  const hasPageBanner = Boolean(pageBackground) && pageBackground !== 'unset' && pageBackground !== 'none';
  const heroColor = hasPageBanner ? theme.page.fontColor : '#FFFFFF';

  return {
    content: {
      '&&': {
        paddingTop: theme.spacing(2),
      },
    },
    hero: {
      position: 'relative',
      overflow: 'hidden',
      color: heroColor,
      backgroundColor: '#163984',
      backgroundImage: hasPageBanner
        ? pageBackground
        : 'linear-gradient(120deg, #0A1F44 0%, #163984 45%, #2969F2 100%)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      padding: theme.spacing(4),
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(3),
      },
    },
    heroTop: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: theme.spacing(2),
    },
    eyebrow: {
      textTransform: 'uppercase',
      letterSpacing: '0.12em',
      fontWeight: 700,
      opacity: 0.85,
    },
    heroTitle: {
      fontWeight: 800,
      letterSpacing: '-0.02em',
      marginTop: theme.spacing(0.5),
    },
    heroText: {
      maxWidth: 760,
      marginTop: theme.spacing(1.5),
      fontSize: '1rem',
      lineHeight: 1.6,
      opacity: 0.92,
    },
    heroActions: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1.5),
      marginTop: theme.spacing(3),
    },
    primaryAction: {
      backgroundColor: '#FFFFFF',
      color: '#0A1F44',
      '&:hover': {
        backgroundColor: alpha('#FFFFFF', 0.88),
      },
    },
    outlineAction: {
      color: heroColor,
      borderColor: alpha(heroColor, 0.6),
      '&:hover': {
        borderColor: heroColor,
        backgroundColor: alpha(heroColor, 0.1),
      },
    },
    // Cards in the same row share the row height (cards use variant="fullHeight"); stacked cards keep their natural height.
    cell: {
      display: 'flex',
      flexDirection: 'column',
      '& > *': {
        flex: 1,
        minWidth: 0,
      },
    },
  };
});

/**
 * DevHub landing page. Layout (top to bottom): service status strip and
 * incident banners, hero, My day + Upcoming events,
 * announcements + discover, recently/top visited, release notes, help.
 */
export const HomePage = () => {
  const classes = useStyles();

  return (
    <SearchContextProvider>
      <Page themeId="home">
        <Content className={classes.content}>
          <ServiceStatusStrip />
          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12}>
              <Card className={classes.hero}>
                <div className={classes.heroTop}>
                  <div>
                    <Typography variant="overline" className={classes.eyebrow}>
                      CAE Internal Developer Portal
                    </Typography>
                    <Typography variant="h3" component="h1" className={classes.heroTitle}>
                      Welcome to DevHub
                    </Typography>
                  </div>
                  <FavoritesMenu className={classes.outlineAction} />
                </div>
                <Typography className={classes.heroText}>
                  Your platform for building software at CAE. Find the tools, templates, tutorials and documentation
                  that support every phase of your development journey, streamline your workflow and help you reach
                  new heights in your projects.
                </Typography>
                <div className={classes.heroActions}>
                  <LinkButton to={GET_STARTED_PATH} variant="contained" className={classes.primaryAction}>
                    Get Started
                  </LinkButton>
                  <LinkButton to="/search" variant="outlined" className={classes.outlineAction}>
                    Search DevHub
                  </LinkButton>
                </div>
              </Card>
            </Grid>
            {/* Every card is variant="fullHeight" and every cell a flex column, so the cards in a row share its height on md+. */}
            <Grid item xs={12} md={8} className={classes.cell}>
              <MyDayCard variant="fullHeight" />
            </Grid>
            <Grid item xs={12} md={4} className={classes.cell}>
              <UpcomingEventsCard variant="fullHeight" />
            </Grid>
            <Grid item xs={12} md={6} className={classes.cell}>
              <HomeAnnouncementsCard title="Announcements" max={5} />
            </Grid>
            <Grid item xs={12} md={6} className={classes.cell}>
              <DiscoverCard />
            </Grid>
            <Grid item xs={12} md={6} className={classes.cell}>
              <RecentlyVisitedCard title="Recently Visited" />
            </Grid>
            <Grid item xs={12} md={6} className={classes.cell}>
              <TopVisitedCard title="Top Visited" />
            </Grid>
            <Grid item xs={12}>
              <PublishedReleaseNotesCard />
            </Grid>
            <Grid item xs={12}>
              <HelpCard />
            </Grid>
          </Grid>
        </Content>
      </Page>
    </SearchContextProvider>
  );
};
